import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../lib/axios';
import { ArrowLeft, Save, ToggleLeft, ToggleRight, Shield } from 'lucide-react';

export default function FeatureToggleManagement() {
  const navigate = useNavigate();
  const [toggles, setToggles] = useState({
    create_legal_opinion: true,
    ec_extractor: true,
    institution_checklist: true,
    online_builder_opinion: true,
    instalegal_surface: false,
    instalegal_social_login: false,
    instalegal_checkout: false,
    instalegal_entitlements: false,
    instalegal_free_subscription: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('global');

  useEffect(() => {
    // Check if user is super_admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'super_admin') {
      alert('Only super administrators can access feature toggles');
      navigate('/dashboard');
      return;
    }
    fetchTenants();
    fetchToggles();
  }, [navigate]);

  useEffect(() => {
    // Fetch toggles when selected tenant changes
    if (selectedTenant) {
      fetchToggles();
    }
  }, [selectedTenant]);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/list`);
      setTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const fetchToggles = async () => {
    try {
      const url = selectedTenant === 'global' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`
        : `${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles?tenant_id=${selectedTenant}`;
      
      const response = await axios.get(url);
      setToggles(response.data.toggles);
    } catch (err) {
      console.error('Error fetching toggles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (featureName) => {
    setToggles(prev => ({
      ...prev,
      [featureName]: !prev[featureName]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`, {
        toggles: toggles,
        tenant_id: selectedTenant
      });
      const scope = selectedTenant === 'global' ? 'global defaults' : `tenant: ${tenants.find(t => t.id === selectedTenant)?.name || selectedTenant}`;
      alert(`Feature toggles updated successfully for ${scope}!`);
    } catch (err) {
      console.error('Error saving toggles:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to update feature toggles';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feature toggles...</p>
        </div>
      </div>
    );
  }

  const features = [
    {
      id: 'create_legal_opinion',
      name: 'Create Legal Opinion',
      description: 'Legal opinion workflow with document upload, extraction, and format selection (Retail/APF)',
      icon: '⚖️',
      color: 'blue'
    },
    {
      id: 'ec_extractor',
      name: 'EC Extractor',
      description: 'Extract data from Encumbrance Certificate documents',
      icon: '🔍',
      color: 'green'
    },
    {
      id: 'institution_checklist',
      name: 'Institutional Checklist',
      description: 'Verify information from official portals (IGRS, MaaBhoomi, etc.)',
      icon: '📋',
      color: 'amber'
    },
    {
      id: 'missing_doc_identifier',
      name: 'Missing Doc Identifier',
      description: 'Identify missing documents required for legal opinion generation',
      icon: '📑',
      color: 'orange'
    },
    {
      id: 'cs_dashboard',
      name: 'CS Dashboard',
      description: 'Company Secretary dashboard for document management and compliance',
      icon: '🏢',
      color: 'indigo'
    },
    {
      id: 'legal_opinion_validator',
      name: 'Legal Opinion Validator',
      description: 'LOVE - Validate legal opinions for compliance and quality',
      icon: '✅',
      color: 'emerald'
    },
    {
      id: 'online_builder_opinion',
      name: 'Online Builder Opinion',
      description: 'Builder, Realtor and Developer workspace for Missing Documents, uploaded evidence and expert opinion services',
      icon: '🏗️',
      color: 'blue'
    },
    {
      id: 'instalegal_surface',
      name: 'InstaLegal Surface',
      description: 'Makes the InstaLegal customer surface available without enabling registration or checkout',
      icon: '🟠',
      color: 'orange'
    },
    {
      id: 'instalegal_social_login',
      name: 'InstaLegal Social Sign-in',
      description: 'Allows configured Google and Facebook sign-in for InstaLegal customers',
      icon: '🔐',
      color: 'blue'
    },
    {
      id: 'instalegal_checkout',
      name: 'InstaLegal Checkout',
      description: 'Allows subscription order creation only in an approved isolated payment test environment',
      icon: '💳',
      color: 'emerald'
    },
    {
      id: 'instalegal_entitlements',
      name: 'InstaLegal Service Entitlements',
      description: 'Requires an active subscription before an InstaLegal customer starts a paid service',
      icon: '🛡️',
      color: 'indigo'
    },
    {
      id: 'instalegal_free_subscription',
      name: 'InstaLegal Free Membership',
      description: 'Keeps annual InstaLegal membership free. Disable only after the paid membership policy is approved and configured.',
      icon: '🎟️',
      color: 'emerald'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            data-testid="feature-toggle-back-button"
            onClick={() => navigate('/admin')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Admin Dashboard</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Feature Toggle Management</h1>
              <p className="text-gray-600">Enable or disable features for tenants (for billing control)</p>
            </div>
            <Shield className="w-12 h-12 text-blue-600" />
          </div>
        </div>

        {/* Tenant Selector */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Tenant / Scope
          </label>
          <select
            data-testid="feature-toggle-tenant-select"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900 bg-white font-medium"
          >
            <option value="global">🌍 Global Defaults (All Tenants)</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                🏢 {tenant.name} ({tenant.subdomain || tenant.id})
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-gray-500">
            {selectedTenant === 'global' 
              ? '⚙️ Changes here will apply to all tenants unless overridden'
              : '💼 Changes here apply only to this tenant (for billing/feature control)'}
          </p>
        </div>

        {/* Feature Cards */}
        <div className="space-y-4 mb-8">
          {features.map(feature => (
            <div
              key={feature.id}
              className={`bg-white rounded-xl shadow-sm p-6 border-2 transition-all ${
                toggles[feature.id] 
                  ? `border-${feature.color}-200 bg-${feature.color}-50/30` 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="text-4xl">{feature.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{feature.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        toggles[feature.id]
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {toggles[feature.id] ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Toggle Button */}
                <button
                  data-testid={`feature-toggle-${feature.id}-button`}
                  onClick={() => handleToggle(feature.id)}
                  className={`relative inline-flex items-center h-12 w-24 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    toggles[feature.id]
                      ? `bg-${feature.color}-600 focus:ring-${feature.color}-500`
                      : 'bg-gray-300 focus:ring-gray-500'
                  }`}
                >
                  <span
                    className={`inline-block w-10 h-10 transform rounded-full bg-white shadow-lg transition-transform ${
                      toggles[feature.id] ? 'translate-x-12' : 'translate-x-1'
                    }`}
                  >
                    {toggles[feature.id] ? (
                      <ToggleRight className="w-10 h-10 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-10 h-10 text-gray-400" />
                    )}
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div>
            <p className="font-semibold text-gray-900">Ready to save changes?</p>
            <p className="text-sm text-gray-600">Changes will take effect immediately on the dashboard</p>
          </div>
          <button
            data-testid="feature-toggle-save-button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-semibold"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
